import { IApiRequest } from "@miplatform/types";
import {
  API_VERSION,
  SUBSCRIPTION_SERVICE,
  X_API_VERSION,
} from "../../../constants";
import { apiOptions, apiRequest } from "../../../utils";

const API_USERS = `${SUBSCRIPTION_SERVICE}/users`;

export const users = {
  getAll: async ({
    filters,
    sort,
    params,
    query,
    signal,
    top,
    skip,
  }: IApiRequest = {}) => {
    const options = await apiOptions({
      url: `${API_USERS}`,
      method: "get",
      filters,
      sort,
      query,
      params,
      signal,
      top,
      skip,
    });
    return apiRequest(options);
  },
  get: async (id: string) => {
    const options = await apiOptions({
      url: `${API_USERS}/${id}`,
      method: "get",
    });
    return apiRequest(options);
  },
  getByWorkPermissions: async ({
    filters,
    sort,
    params,
    signal,
    top,
    skip,
    query,
  }: IApiRequest = {}) => {
    const options = await apiOptions({
      url: `${API_USERS}/list/by-work-permissions`,
      method: "get",
      filters,
      sort,
      params,
      signal,
      top,
      skip,
      query,
    });
    return apiRequest(options);
  },
  updateProfile: async ({ data, toastMessageConfig }: IApiRequest) => {
    const options = await apiOptions({
      url: `${SUBSCRIPTION_SERVICE}/user/profile`,
      method: "patch",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  createUser: async ({ data, toastMessageConfig }: IApiRequest) => {
    const options = await apiOptions({
      url: `${API_USERS}`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  getUpdateUserOptions: async ({ id, data }: IApiRequest) => {
    const options = await apiOptions({
      url: `${API_USERS}/${id}`,
      method: "patch",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    return options;
  },
  updateUser: async ({ id, data, toastMessageConfig }: IApiRequest) => {
    const options = await users.getUpdateUserOptions({ id, data });
    return apiRequest(options, toastMessageConfig);
  },
  deleteUser: async ({ id, data, toastMessageConfig }: IApiRequest) => {
    const options = await apiOptions({
      url: `${API_USERS}/${id}`,
      method: "delete",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  activateUser: async ({ id, data, toastMessageConfig }: IApiRequest) => {
    const options = await apiOptions({
      url: `${API_USERS}/${id}/activateUser`,
      method: "put",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  deactivateUser: async ({ id, data, toastMessageConfig }: IApiRequest) => {
    const options = await apiOptions({
      url: `${API_USERS}/${id}/deactivateUser`,
      method: "put",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  resendInvitation: async ({ id, toastMessageConfig }: IApiRequest) => {
    const options = await apiOptions({
      url: `${API_USERS}/${id}/resendInvitation`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
    });
    return apiRequest(options, toastMessageConfig);
  },
  listByPermission: async ({
    permission,
    filters,
    sort,
    params,
    signal,
    top,
    skip,
  }: IApiRequest & { permission: string }) => {
    const options = await apiOptions({
      url: `${API_USERS}/list/by-permission`,
      query: {
        permission,
      },
      filters,
      sort,
      params,
      signal,
      top,
      skip,
      method: "get",
      headers: { [X_API_VERSION]: API_VERSION },
    });

    return apiRequest(options);
  },
  updateActivityStatus: async ({
    id,
    data,
    toastMessageConfig,
  }: IApiRequest) => {
    const options = await apiOptions({
      url: `${API_USERS}/activity/${id}`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  createUserAvailabilityLog: async ({
    data,
    toastMessageConfig,
  }: IApiRequest) => {
    const options = await apiOptions({
      url: `${API_USERS}/availability-log`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  getUserAvailabilityLogs: async ({
    filters,
    sort,
    params,
    signal,
    top,
    skip,
  }: IApiRequest) => {
    const options = await apiOptions({
      url: `${API_USERS}/availability-log/all`,
      method: "get",
      headers: { [X_API_VERSION]: API_VERSION },
      filters,
      sort,
      params,
      signal,
      top,
      skip,
    });
    return apiRequest(options);
  },
  deleteUserAvailabilityLog: async ({
    id,
    data,
    toastMessageConfig,
  }: IApiRequest) => {
    const options = await apiOptions({
      url: `${API_USERS}/availability-log/${id}`,
      method: "delete",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
};
